import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { ComponentProps, useMemo } from "react";
import dayjs from "dayjs";
import { ConditionalLink } from "components/conditionalLink";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);

export type LockedDatePickerProps = ComponentProps<typeof DateInput> & {
  bypassLockDate?: boolean;
};

export const LockDateToolTipContent = ({
  text = "This date is from a locked period and therefore cannot be selected.",
}: {
  text?: string;
}) => {
  const { link } = useConstructInternalLink();
  const { bookkeeping_channel } = useCurrentEntity() || {};

  return (
    <span>
      {text}
      <br />
      <br />
      Please either update the lock date in{" "}
      <ConditionalLink
        target="_blank"
        to={link("/settings/books-settings")}
        className="!t-underline t-text-white"
      >
        settings
      </ConditionalLink>{" "}
      or reach out to us via{" "}
      <ConditionalLink
        target="_blank"
        to={`/chat/${bookkeeping_channel}`}
        className="!t-underline t-text-white"
      >
        chat
      </ConditionalLink>{" "}
      for assistance.
    </span>
  );
};

export const LockedDatePicker = ({
  bypassLockDate,
  minDate,
  ...props
}: LockedDatePickerProps) => {
  const entityId = useCurrentEntityId();

  const { data: lockDateData, isLoading } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId || bypassLockDate }
  );

  const effectiveMinDate = useMemo(() => {
    if (bypassLockDate) {
      return minDate;
    }

    if (lockDateData?.accounting_lock_date) {
      const lockDate = dayjs(lockDateData.accounting_lock_date)
        .add(1, "day")
        .toDate();

      if (minDate && dayjs(minDate).isAfter(lockDate)) {
        return minDate;
      }

      return lockDate;
    }

    return minDate;
  }, [lockDateData, minDate, bypassLockDate]);

  return (
    <DateInput
      {...props}
      minDate={effectiveMinDate}
      disabled={props.disabled || isLoading}
      getDisabledTooltipContent={(date, minDate, maxDate) => {
        if (bypassLockDate) {
          return "";
        }

        if (lockDateData?.accounting_lock_date) {
          const lockDate = dayjs(lockDateData.accounting_lock_date).toDate();
          if (dayjs(date).isSameOrBefore(lockDate)) {
            return <LockDateToolTipContent />;
          }
        }

        return "";
      }}
    />
  );
};
